<template>
    <div :class="{ 'my-4': label }">
        <div
            v-if="label"
            class="flex items-center justify-between"
        >
            <label
                class="mb-1 block text-sm font-medium"
                :for="name"
                >{{ $t(label) }}
                <span
                    v-if="required === true"
                    class="text-danger-500"
                    >*
                </span>
            </label>
            <Tooltip
                v-if="help"
                class="ml-2"
                bg="dark"
                size="md"
                position="left"
            >
                <div class="text-sm text-gray-200">{{ $t(help) }}</div>
            </Tooltip>
        </div>
        <div
            class="datepicker relative"
            :class="{
                'no-year': noYearMode,
            }"
        >
            <input
                ref="datepickerInput"
                type="text"
                class="form-input w-60 pl-9 font-medium text-gray-500 hover:text-gray-600 focus:border-gray-300"
                :autocomplete="autocomplete ? 'on' : 'off'"
                :class="pickerInputClass"
                :name="name"
                :placeholder="placeholder ? $t(placeholder) : ''"
            />
            <div
                class="pointer-events-none absolute inset-0 right-auto flex items-center"
            >
                <svg
                    class="ml-3 h-4 w-4 fill-current text-gray-500"
                    :class="pickerIconClass"
                    viewBox="0 0 16 16"
                >
                    <path
                        d="M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V3a1 1 0 00-1-1zm-1 12H2V6h12v8z"
                    />
                </svg>
            </div>
        </div>
        <div
            v-if="bottomHelpText"
            class="mt-2 text-xs"
        >
            {{ $t(bottomHelpText) }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { Tooltip } from '@src/components'
import { DatepickerValue } from '@src/types'
import { useVModels } from '@vueuse/core'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(
    defineProps<{
        modelValue: DatepickerValue | unknown
        autocomplete?: boolean
        bottomHelpText?: string | null
        dateFormat?: string
        dateFrom?: DatepickerValue | unknown | null
        dateTo?: DatepickerValue | unknown | null
        datemode?: string
        defaultDate?: DatepickerValue
        enableTime?: boolean
        help?: string | null
        label?: string | null
        minuteIncrement?: number
        name: string
        pickerIconClass?: string
        pickerInputClass?: string
        placeholder?: string
        required?: boolean
        time24hr?: boolean
    }>(),
    {
        autocomplete: false,
        bottomHelpText: null,
        dateFormat: 'Y-m-d',
        dateFrom: undefined,
        dateTo: undefined,
        datemode: 'single',
        defaultDate: undefined,
        enableTime: false,
        help: null,
        label: null,
        minuteIncrement: 5,
        pickerIconClass: '',
        pickerInputClass: '',
        placeholder: '',
        time24hr: true,
    }
)

const emit = defineEmits([
    'update:modelValue',
    'update:dateFrom',
    'update:dateTo',
])

const { modelValue, dateFrom, dateTo } = useVModels(props, emit)

const datepickerInput = ref<HTMLInputElement | null>(null)
let fpInstance: flatpickr.Instance | null = null

const noYearMode = computed(() => {
    return props.dateFormat === 'm-d'
})

onMounted(() => {
    if (!datepickerInput.value) return

    fpInstance = flatpickr(datepickerInput.value, {
        allowInput: true,
        mode: (props.datemode as any) || 'single',
        static: true,
        monthSelectorType: 'static',
        dateFormat: props.dateFormat || 'Y-m-d',
        enableTime: !!props.enableTime,
        // Ends up as the hidden minute input's `step`: a value off that grid (e.g. 14:37 with
        // step 5) fails native validation and silently blocks the surrounding form's submit.
        minuteIncrement: props.minuteIncrement,
        time_24hr: !!props.time24hr,
        defaultDate:
            (modelValue.value as any) || props.defaultDate || undefined,
        prevArrow:
            '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M5.4 10.8l1.4-1.4-4-4 4-4L5.4 0 0 5.4z" /></svg>',
        nextArrow:
            '<svg class="fill-current" width="7" height="11" viewBox="0 0 7 11"><path d="M1.4 10.8L0 9.4l4-4-4-4L1.4 0l5.4 5.4z" /></svg>',
        onChange(selectedDates, dateStr) {
            if (!dateStr) {
                modelValue.value = null
                dateFrom.value = null
                dateTo.value = null
                return
            }

            if (props.datemode === 'range' && selectedDates.length === 2) {
                modelValue.value = null
                dateFrom.value = selectedDates[0].toISOString()
                dateTo.value = selectedDates[1].toISOString()
            } else {
                modelValue.value = dateStr
            }
        },
    })
})

watch(
    () => modelValue.value,
    (newVal) => {
        if (fpInstance) {
            fpInstance.setDate((newVal as any) || '', false)
        }
    }
)

onBeforeUnmount(() => {
    if (fpInstance) {
        fpInstance.destroy()
    }
})
</script>
